import { daysBetween, type IsoDate } from './calendar';
import { assertPaise, type Paise, percentOf } from './money';
import type { ReturnForm } from './returns';

/**
 * Late fees and interest (sections 47 and 50 of the CGST Act, and the matching State Acts).
 *
 * Late fee: ₹50 a day (₹25 CGST + ₹25 SGST) for each day a return is late, or ₹20 a day (₹10 + ₹10)
 * for a NIL return (Notification 76/2018-CT; 4/2018-CT for GSTR-1), up to a cap set by the previous
 * year's aggregate turnover: ₹500 for NIL returns; otherwise ₹2,000 up to ₹1.5 crore, ₹5,000 up to
 * ₹5 crore and ₹10,000 above (Notifications 19/2021 and 20/2021-CT, for GSTR-3B and GSTR-1). GSTR-4 is
 * capped at ₹2,000 (₹500 if NIL; 21/2021-CT). CMP-08 has no late fee, only interest. There's no IGST
 * late fee (IGST Act section 20).
 *
 * Interest: 18% a year (section 50(1)) on tax paid late in cash, simple, for each day after the due
 * date until it's paid. Since the January 2026 period GSTN works it out itself, leaving out cash that
 * sat in the cash ledger from the due date (rule 88B): Phase 10 follows the portal's figure.
 */

/** Aggregate annual turnover (previous year): up to ₹1.5 crore, up to ₹5 crore, or above. */
export const TURNOVER_BANDS = ['upto_1_5cr', 'upto_5cr', 'above_5cr'] as const;
export type TurnoverBand = (typeof TURNOVER_BANDS)[number];

const RUPEE = 100;

/** Per day, CGST and SGST together. */
export const LATE_FEE_PER_DAY: Paise = 50 * RUPEE;
export const NIL_LATE_FEE_PER_DAY: Paise = 20 * RUPEE;
export const NIL_LATE_FEE_CAP: Paise = 500 * RUPEE;

const CAP_BY_BAND: Record<TurnoverBand, Paise> = {
  upto_1_5cr: 2_000 * RUPEE,
  upto_5cr: 5_000 * RUPEE,
  above_5cr: 10_000 * RUPEE,
};

/** Forms charged a late fee for each day they're late. */
export function hasLateFee(form: ReturnForm): boolean {
  return form !== 'cmp08';
}

export function lateFeePerDay(form: ReturnForm, nil: boolean): Paise {
  if (!hasLateFee(form)) return 0;
  return nil ? NIL_LATE_FEE_PER_DAY : LATE_FEE_PER_DAY;
}

/** The most a return's late fee can reach. Turnover unknown: the smallest band (most EkApp users). */
export function lateFeeCap(form: ReturnForm, nil: boolean, turnoverBand: TurnoverBand = 'upto_1_5cr'): Paise {
  if (!hasLateFee(form)) return 0;
  if (nil) return NIL_LATE_FEE_CAP;
  if (form === 'gstr4') return CAP_BY_BAND.upto_1_5cr;
  return CAP_BY_BAND[turnoverBand];
}

/** The late fee for a return filed `daysLate` days after its due date (0 or less: on time). */
export function lateFee(input: {
  form: ReturnForm;
  daysLate: number;
  nil?: boolean;
  turnoverBand?: TurnoverBand;
}): Paise {
  if (!Number.isInteger(input.daysLate)) throw new TypeError('daysLate must be a whole number of days');
  if (input.daysLate <= 0) return 0;
  const nil = input.nil ?? false;
  return Math.min(input.daysLate * lateFeePerDay(input.form, nil), lateFeeCap(input.form, nil, input.turnoverBand));
}

/** Interest on tax paid late in cash: 18% a year, simple, for each day after the due date until it's paid. */
export const INTEREST_PERCENT_A_YEAR = 18;

export function interestOnLateTax(input: {
  tax: Paise;
  dueOn: IsoDate;
  paidOn: IsoDate;
  percentAYear?: number;
}): Paise {
  assertPaise(input.tax, 'tax');
  if (input.tax < 0) throw new RangeError('tax must not be negative');
  const days = daysBetween(input.dueOn, input.paidOn);
  if (days <= 0 || input.tax === 0) return 0;
  const percent = input.percentAYear ?? INTEREST_PERCENT_A_YEAR;
  return percentOf(input.tax, (percent * days) / 365);
}
